import { Injectable, signal, ViewChild } from '@angular/core';
import { STChange, STComponent } from '@delon/abc/st';
import { _HttpClient } from '@delon/theme';
import {
  IImportSource,
  SourceTypes
} from '@shared-feature/components/import-excel-drawer/model';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

import { ConfirmService } from '../../../shared/services/confirm.service';

@Injectable()
export abstract class ImportExcelUtils {
  @ViewChild('st') st!: STComponent;

  importSources: IImportSource[];

  requiredFields: string[] = [];

  selectedImportSource = signal<SourceTypes | null>(null);

  showImportDrawer = signal<boolean>(false);

  selectedRows = signal<any[]>([]);

  loading = signal<boolean>(false);

  deleting = signal<boolean>(false);

  showFieldMapperDialog = signal<boolean>(false);

  list: any[] = [];

  userMapperData: any = {};

  // Store the mapper data before opening the field mapper dialog for cancel restoration
  private _previousUserMapperData: any = {};

  sampleDownloadUrl = '';

  skipDuplicates = true;

  fieldMapperHeaders: string[] = [];

  // Abstract methods to be implemented by child classes
  abstract getHeaderRow(): Promise<string[]>;

  abstract processImportData(
    headerRow: string[],
    sourceType: string | null
  ): Promise<any[]>;

  abstract getSaveItemsEndpoint(): string;

  abstract getDuplicateCheckEndpoint(): string;

  abstract loadList(): void;

  protected updateListItem(item: any, field: string, newValue: any): void {
    const index = this.list.findIndex(listItem => listItem.Code === item.Code);
    if (index !== -1) {
      this.list[index] = { ...this.list[index], [field]: newValue };
    } else {
      console.warn('Item not found in list:', item);
    }
  }

  protected validateItem(item: any): boolean {
    return this.requiredFields.some(field => item[field]);
  }

  /**
   * Get the display name for a field. Child classes should override this
   * to provide user-friendly names from their column definitions.
   * Default implementation returns the field name as-is.
   */
  protected getFieldDisplayName(fieldName: string): string {
    return fieldName;
  }

  constructor(
    allowedSources: SourceTypes[] | null,
    requiredFields: string[] = [],
    protected msg: NzMessageService,
    protected http: _HttpClient,
    protected confirmSrv: ConfirmService,
    protected modalSrv: NzModalService
  ) {
    this.importSources = this.generateImportSources(allowedSources);
    this.requiredFields = requiredFields;
  }

  private generateImportSources(
    allowedSources: SourceTypes[] | null
  ): IImportSource[] {
    const allSources: IImportSource[] = [
      { label: 'SQL Accounting', value: SourceTypes.SQLAccount },
      { label: 'UBS', value: SourceTypes.UBS },
      { label: 'Autocount Desktop', value: SourceTypes.AutoCountDesktop },
      { label: 'Autocount Cloud Accounting', value: SourceTypes.Autocount },
      { label: 'Quickbooks Desktop', value: SourceTypes.QuickBooksDesktop },
      { label: 'Xero', value: SourceTypes.Xero },
      { label: 'Quickbooks Online', value: SourceTypes.QuickBooksOnline },
      { label: 'MYOB', value: SourceTypes.MYOB }
    ];

    // If allowedSources is null, return empty
    if (allowedSources === null) {
      return [];
    }

    // If no allowedSources are provided, return all sources
    if (allowedSources.length === 0) {
      return allSources;
    }

    // Filter the sources based on allowedSources
    return allSources.filter(source => allowedSources.includes(source.value));
  }

  // Protected method to filter footer rows
  protected filterFooterRows(rawData: any[], headerRow: string[]): any[] {
    return rawData.filter(row => !this.isFooterRow(row, headerRow));
  }

  // Protected method with default footer detection (can be overridden)
  protected isFooterRow(row: any, headerRow: string[]): boolean {
    const firstColumnValue = row[headerRow[0]]?.toString().trim().toLowerCase();

    // Define common footer keywords
    // const footerKeywords: any[] = [];
    // const hasFooterKeyword = footerKeywords.some(keyword =>
    //   firstColumnValue?.includes(keyword)
    // );
    const hasFooterKeyword = true;

    // Check if all other columns are empty
    const otherColumnsEmpty = headerRow
      .slice(1)
      .every(field => !row[field] || row[field].toString().trim() === '');

    // Default: Row is a footer if it has a footer keyword AND other columns are empty
    return hasFooterKeyword && otherColumnsEmpty;
  }

  protected mapRowToTarget(row: any, sourceType: SourceTypes | null) {
    // Get default mappings from the child class
    const defaultMappings = this.getDefaultMappings(row, sourceType);

    // User-defined mappings from the modal
    const userMappings = this.userMapperData || {};

    // Merge user-defined mappings with default mappings
    const mergedMappings = { ...defaultMappings };

    // Normalize the row keys to lowercase
    const normalizedRow = Object.keys(row).reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = row[key];
        return acc;
      },
      {} as Record<string, any>
    );

    // Get boolean fields from the child class (optional customization)
    const booleanFields = this.getBooleanFields();

    // Apply user-defined mappings with boolean conversion
    for (const [targetField, csvColumn] of Object.entries(userMappings)) {
      if (typeof csvColumn === 'string') {
        const normalizedCsvColumn = csvColumn.toLowerCase();
        if (normalizedCsvColumn in normalizedRow) {
          let value = normalizedRow[normalizedCsvColumn];
          // Apply boolean conversion if the target field is in booleanFields
          if (booleanFields.includes(targetField)) {
            value = this.toBoolean(value);
          }
          mergedMappings[targetField as keyof typeof mergedMappings] = value;
        }
      }
    }

    return mergedMappings;
  }

  // Abstract method for child classes to implement their specific mappings
  protected abstract getDefaultMappings(
    row: any,
    sourceType: SourceTypes | null
  ): Record<string, any>;

  protected getBooleanFields(): string[] {
    return [];
  }

  // Generalized import function
  async onImportData(
    importComponent: any,
    selectedImportSource: SourceTypes | null,
    deferredVatSetting?: boolean,
    headerRowNo?: number
  ): Promise<void> {
    if (!importComponent?.data?.length) {
      console.warn('No data to import');
      return;
    }

    if (deferredVatSetting === false) {
      let hasDeferred = importComponent?.data?.some((o:any) => o?.isDeferredTax?.toString().toLowerCase() === 'true');
      if (hasDeferred) {
        this.msg.error('Cannot import deferred VAT transactions while the Deferred VAT option is turned off. Please enable option to continue.');
        return;
      }
    }

    try {
      const sourceType = selectedImportSource;
      const headerRow = await this.getHeaderRow();

      this.list = await this.processImportData(headerRow, sourceType);

      // console.log(this.list)
      this.showImportDrawer.set(false);
    } catch (error) {
      this.msg.error('Failed to process import data');
    }
  }

  async onFieldMapping() {
    // Save the current mapper data before opening the dialog for potential restoration on cancel
    this._previousUserMapperData = { ...this.userMapperData };
    this.fieldMapperHeaders = await this.getHeaderRow();
    this.showFieldMapperDialog.set(true);
  }

  onChangeImportSource($event: any) {
    this.selectedImportSource.set($event);
  }

  onShowImportDrawer() {
    this.showImportDrawer.set(true);
  }

  onCloseImportDrawer() {
    this.showImportDrawer.set(false);
  }

  toBoolean(value: any): boolean {
    if (typeof value === 'string') {
      const normalizedValue = value.toLowerCase().trim();
      return (
        normalizedValue === 'true' ||
        normalizedValue === 't' ||
        normalizedValue === '1' ||
        normalizedValue === 'yes'
      );
    }
    return !!value;
  }

  onMapperDialogSave(
    data: any,
    importComponent: any,
    headerRowNo?: number
  ): void {
    this.showFieldMapperDialog.set(false);
    this.userMapperData = data;

    if (this.list.length > 0) {
      // Call onImportData but merge the result with existing edits
      this.onImportDataWithMerge(
        importComponent,
        this.selectedImportSource(),
        headerRowNo
      );
    }
  }

  private async onImportDataWithMerge(
    importComponent: any,
    selectedImportSource: SourceTypes | null,
    headerRowNo?: number
  ): Promise<void> {
    if (!importComponent?.data?.length) {
      console.warn('No data to import');
      return;
    }

    try {
      const sourceType = selectedImportSource;
      const headerRow = await this.getHeaderRow();
      const newList = await this.processImportData(headerRow, sourceType);

      const mergedList = this.mergeLists(
        this.list,
        newList,
        this.userMapperData
      );

      this.list = mergedList;
      if (this.st) {
        this.st.reset(this.list); // Explicitly reset the data
      }
      this.showImportDrawer.set(false);
    } catch (error) {
      this.msg.error('Failed to process import data');
    }
  }

  private mergeLists(
    existingList: any[],
    newList: any[],
    mappings: any
  ): any[] {
    return existingList.map(existingItem => {
      const possibleKeys = ['Code', 'AccountCode'];
      // Find the corresponding item in newList by unique identifier (e.g., Code)
      const newItem = newList.find(item =>
        possibleKeys.some(key => item[key] && item[key] === existingItem[key])
      );

      if (!newItem) {
        return { ...existingItem }; // No match in newList, keep existing item unchanged
      }

      // Start with the existing item to preserve all manual edits
      const mergedItem = { ...existingItem };

      // Apply only the fields explicitly remapped by the field mapper
      for (const [targetField, csvColumn] of Object.entries(mappings)) {
        if (newItem[targetField] !== undefined) {
          mergedItem[targetField] = newItem[targetField];
        }
      }

      return mergedItem;
    });
  }

  async onMapperDialogCancel(): Promise<void> {
    // Restore the previous mapper data
    this.userMapperData = { ...this._previousUserMapperData };
    this.showFieldMapperDialog.set(false);

    // If there's data in the list and we had previous mappings, re-apply them to restore the state
    if (this.list.length > 0 && Object.keys(this._previousUserMapperData).length > 0) {
      await this.reapplyMappings();
    }
  }

  private async reapplyMappings(): Promise<void> {
    try {
      const sourceType = this.selectedImportSource();
      const headerRow = await this.getHeaderRow();
      const newList = await this.processImportData(headerRow, sourceType);

      const mergedList = this.mergeLists(
        this.list,
        newList,
        this.userMapperData
      );

      this.list = mergedList;
      if (this.st) {
        this.st.reset(this.list); // Explicitly reset the data
      }
    } catch (error) {
      console.error('Failed to reapply mappings:', error);
    }
  }

  onDelete(removeKey: string): void {
    this.confirmSrv.confirmDelete().then(() => this.remove(removeKey));
  }

  onGridChange(e: STChange): void {
    if (e.type === 'checkbox') {
      if (e.checkbox) this.selectedRows.set(e.checkbox);
    }
  }

  onSaveItems(st: STComponent, removeKey: string): void {
    if (!st._data.length) return;

    const dataToSave: any =
      this.selectedRows().length > 0 ? this.selectedRows() : st.data;

    if (dataToSave.length > 1000) {
      this.msg.warning(
        'You can only import a maximum of 1000 items at a time!'
      );
      return;
    }

    if (this.hasMissingRequiredFields(dataToSave)) {
      const missingFields = this.requiredFields.filter(field =>
        dataToSave.some((item: any) => !item[field])
      );

      if (missingFields.length > 0) {
        // Map field names to user-friendly display names
        const displayNames = missingFields.map(field => this.getFieldDisplayName(field));
        this.msg.warning(
          `Missing required fields: ${displayNames.join(', ')}`
        );
        return;
      }
      this.msg.warning('You should fill all the required fields!');
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    const endpoint = this.getDuplicateCheckEndpoint();
    this.http.post(endpoint, { data: dataToSave }).subscribe({
      next: (response: any) => {
        if (response) {
          //there are duplicates
          this.loading.set(false);
          this.modalSrv.confirm({
            nzTitle: 'Overwrite Records',
            nzContent:
              'Matching data found in the template. Do you want to overwrite the existing records?',
            nzOkText: 'Yes',
            nzOkType: 'primary',
            nzCancelText: 'Skip',
            nzOnOk: () => {
              this.skipDuplicates = false;
              this.handleSave(dataToSave, removeKey);
            },
            nzOnCancel: () => {
              this.skipDuplicates = true;
              this.handleSave(dataToSave, removeKey);
            }
          });
        } else {
          this.handleSave(dataToSave, removeKey);
        }
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  handleSave(dataToSave: any, removeKey: string) {
    this.loading.set(true);
    const endpoint = this.getSaveItemsEndpoint();
    this.http
      .post(`${endpoint}?skipDuplicates=${this.skipDuplicates}`, {
        data: dataToSave
      })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.remove(removeKey);
          this.loadList();
        },
        error: err => {
          this.loading.set(false);
          if(!err?.error?.message){
            this.msg.error('Failed to save items. Please try again.');
          }
        }
      });
  }

  updateItem() {}

  hasMissingRequiredFields(data: any[]): boolean {
    return data.some(item => {
      return this.requiredFields.some(field => !item[field]);
    });
  }

  remove(key: string): void {
    if (this.selectedRows().length > 0) {
      this.deleting.set(true);
      this.list = this.list.filter(
        a => !this.selectedRows().some(e => e[key] === a[key])
      );
      this.selectedRows.set([]);
      this.deleting.set(false);
    }
  }
}
